/**
 * The message a card sits in, for parts that describe other parts: a
 * `data-approval-review` says what an approval card should show beyond the
 * raw tool arguments, keyed by approval id; a `data-change` is what a tool
 * call wrote (with Undo), and the call's own row carries it. Hosts that
 * render an assistant message provide it; without it cards fall back to the
 * raw arguments and the change their own result carries.
 */
import {
  createContext,
  useContext,
  useMemo,
  type FC,
  type ReactNode
} from "react";

import type { AgentChange, ApprovalReview } from "@/features/agents/types";

import { OWN_CARD_TOOLS } from "./toolTitles";

type Reviews = Readonly<Record<string, ApprovalReview>>;
type Changes = ReadonlyMap<string, AgentChange>;

const NO_REVIEWS: Reviews = {};
const NO_CHANGES: Changes = new Map();

type Parts = {
  reviews: Reviews;
  /** `data-change` parts by change id. */
  changes: Changes;
  /** The same, by the tool call that wrote them. */
  changesByCall: Changes;
};

const MessagePartsContext = createContext<Parts>({
  reviews: NO_REVIEWS,
  changes: NO_CHANGES,
  changesByCall: NO_CHANGES
});

type PartLike = {
  type: string;
  id?: string;
  data?: unknown;
  toolCallId?: string;
  toolName?: string;
  state?: string;
  output?: unknown;
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

/** `data-approval-review` parts by approval id (the last one for an id wins). */
export const approvalReviewsOf = (parts: readonly PartLike[]): Reviews => {
  let out: Record<string, ApprovalReview> | null = null;
  parts.forEach((part) => {
    const review =
      part.type === "data-approval-review"
        ? (part.data as ApprovalReview | undefined)
        : undefined;
    if (review?.approvalId) (out ??= {})[review.approvalId] = review;
  });
  return out ?? NO_REVIEWS;
};

/** A recorded, undoable change (not a document's tracked change). */
export const isChangeLike = (v: unknown): v is AgentChange =>
  isRecord(v) &&
  typeof v.id === "string" &&
  v.id !== "" &&
  typeof v.label === "string";

/** The change a tool result carries (`ChangeTrackingMiddleware`), if any. */
export const changeOfOutput = (output: unknown): AgentChange | null =>
  isRecord(output) && !("error" in output) && isChangeLike(output.change)
    ? output.change
    : null;

/** `data-change` parts, by change id and by the tool call that wrote them. */
export const changesOf = (
  parts: readonly PartLike[]
): { changes: Changes; changesByCall: Changes } => {
  let byId: Map<string, AgentChange> | null = null;
  let byCall: Map<string, AgentChange> | null = null;
  parts.forEach((part) => {
    if (part.type !== "data-change" || !isChangeLike(part.data)) return;
    (byId ??= new Map()).set(part.data.id, part.data);
    const call = part.data.tool_call_id;
    if (call) (byCall ??= new Map()).set(call, part.data);
  });
  return { changes: byId ?? NO_CHANGES, changesByCall: byCall ?? NO_CHANGES };
};

const isToolPart = (part: PartLike): boolean =>
  part.type === "dynamic-tool" || part.type.startsWith("tool-");

const toolNameOfPart = (part: PartLike): string =>
  part.type === "dynamic-tool"
    ? part.toolName ?? ""
    : part.type.replace(/^tool-/, "");

/**
 * The change ids some tool row in the message shows (with its Undo), so no
 * separate change card is needed. Tools with a card of their own keep the
 * change card.
 */
export const adoptedChangesOf = (
  parts: readonly PartLike[]
): ReadonlySet<string> => {
  const calls = new Set<string>();
  const out = new Set<string>();
  parts.forEach((part) => {
    if (!isToolPart(part) || OWN_CARD_TOOLS.has(toolNameOfPart(part))) return;
    if (part.toolCallId) calls.add(part.toolCallId);
    if (part.state !== "output-available") return;
    const own = changeOfOutput(part.output);
    if (own) out.add(own.id);
  });
  if (calls.size) {
    parts.forEach((part) => {
      if (part.type !== "data-change" || !isChangeLike(part.data)) return;
      const call = part.data.tool_call_id;
      if (call && calls.has(call)) out.add(part.data.id);
    });
  }
  return out;
};

export const MessagePartsProvider: FC<{
  parts: readonly PartLike[];
  children: ReactNode;
}> = ({ parts, children }) => {
  const value = useMemo(
    () => ({ reviews: approvalReviewsOf(parts), ...changesOf(parts) }),
    [parts]
  );
  return (
    <MessagePartsContext.Provider value={value}>
      {children}
    </MessagePartsContext.Provider>
  );
};

/** The review for an approval card, when the server sent one. */
export const useApprovalReview = (
  approvalId: string | null | undefined
): ApprovalReview | null => {
  const { reviews } = useContext(MessagePartsContext);
  return approvalId ? reviews[approvalId] ?? null : null;
};

type ToolPartLike = { toolCallId?: string; state: string; output?: unknown };

/**
 * What a tool call wrote, as an undoable change: the message's `data-change`
 * part (it carries the reconciled status), else the copy in the call's own
 * result.
 */
export const useToolChange = (part: ToolPartLike): AgentChange | null => {
  const { changes, changesByCall } = useContext(MessagePartsContext);
  const own =
    part.state === "output-available" ? changeOfOutput(part.output) : null;
  return (
    (own ? changes.get(own.id) : undefined) ??
    (part.toolCallId ? changesByCall.get(part.toolCallId) : undefined) ??
    own
  );
};
